import StudioBrand from './StudioBrand';
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, RefreshCw, ShieldCheck, Undo2 } from 'lucide-react';
import { loadCloudReviews, runPlatformClinicalReviewWorkflow } from '../lib/reviewApi';
import { CLINICAL_REVIEW_COVERAGE_AREAS, clinicalReviewCoverageSummary } from '../../../src/data/clinicalReviewCoverageRegister.js';

function formatDate(value) {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not recorded' : date.toLocaleString();
}

function statusLabel(status) {
  if (status === 'submitted') return 'Awaiting review';
  if (status === 'approved') return 'Approved';
  if (status === 'draft') return 'Draft / returned';
  return status || 'Draft';
}

export default function ClinicalReviewInboxStudio({ workspaceSwitcher = null }) {
  const [submissions, setSubmissions] = useState([]);
  const [comments, setComments] = useState({});
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const response = await loadCloudReviews();
      setSubmissions(response.clinicalSubmissions || []);
      setNotice('');
    } catch (error) {
      setNotice(error.message || 'Clinical review submissions could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const awaiting = useMemo(() => submissions.filter((item) => item.status === 'submitted'), [submissions]);
  const approved = useMemo(() => submissions.filter((item) => item.status === 'approved'), [submissions]);

  const act = async (submission, action) => {
    const reviewerUserId = submission.reviewerUserId;
    if (!reviewerUserId || busyId) return;
    setBusyId(reviewerUserId);
    try {
      const response = await runPlatformClinicalReviewWorkflow(reviewerUserId, action, comments[reviewerUserId] || '');
      setSubmissions(response.clinicalSubmissions || []);
      setComments((current) => ({ ...current, [reviewerUserId]: '' }));
      setNotice(action === 'approve_submission' ? 'Clinical review scope approved.' : 'Clinical review scope returned for changes.');
    } catch (error) {
      setNotice(error.message || 'Clinical review workflow action failed.');
    } finally {
      setBusyId(null);
    }
  };

  return <div className="app-shell">
    <header className="topbar"><div className="brand"><span className="brand-mark"><ShieldCheck/></span><div><strong><StudioBrand /></strong><small>Clinical review inbox</small></div></div><div className="topbar-actions"><button className="button secondary" disabled={loading} onClick={load}><RefreshCw size={17}/>Refresh</button></div></header>
    {notice && <div className="studio-notice" role="status"><span>{notice}</span><button onClick={()=>setNotice('')} aria-label="Dismiss notification">×</button></div>}
    <div className="workspace">
      <aside className="catalogue">{workspaceSwitcher}<div className="catalogue-intro"><p className="eyebrow">Platform administration</p><h1>Clinical review inbox</h1><p>Approve completed reviewer scopes or return them for changes. Submitted content remains locked until you act.</p></div><div className="journey-studio-stat"><ShieldCheck size={20}/><div><strong>{awaiting.length} awaiting review</strong><span>{approved.length} approved reviewer scopes</span></div></div></aside>
      <main className="review-workspace">
        <div className="role-toolbar"><div><p className="eyebrow">Clinical assurance</p><h1>Reviewer submissions</h1><div className="meta-row"><span>{submissions.length} total scopes</span><span>{awaiting.length} awaiting decision</span></div></div></div>
        <section className="trust-config-panel" aria-label="Clinical review completeness audit">
          <div className="trust-panel-heading"><div><p className="eyebrow">Full patient-content clinical assurance</p>
            <h2>Clinical-review coverage register</h2>
            <p>This register is a source coverage audit, not an approval of the wording. A translation review or a generic Journey approval cannot replace clinical review of every patient-facing statement.</p></div></div>
          <div className="meta-row"><span>{clinicalReviewCoverageSummary().total} source areas audited</span>
            <span>{clinicalReviewCoverageSummary().gaps} unimplemented coverage areas</span>
            <span>{clinicalReviewCoverageSummary().partial} partially covered</span></div>
          <div style={{ overflowX: 'auto' }}><table><thead><tr><th>Patient clinical source</th><th>Current coverage</th><th>Reviewer / remaining gap</th></tr></thead>
            <tbody>{CLINICAL_REVIEW_COVERAGE_AREAS.map(item => <tr key={item.id}>
              <td><strong>{item.name}</strong><p><small>{item.source}</small></p></td>
              <td>{item.state === 'gap' ? 'Coverage missing' : item.state === 'partial' ? 'Partial — needs audit' : 'Complete'}</td>
              <td><strong>{item.reviewer}</strong><p>{item.issue}</p></td>
            </tr>)}</tbody></table></div>
          <p>Publication gate: current Trust Journey review is enforced for new Trust releases. National clinical sources, role-detail provenance, English-only Easy Read and print content require separate sprints before full-app clinical assurance can be claimed.</p>
        </section>
        {loading ? <section className="trust-config-panel"><p>Loading clinical reviewer submissions…</p></section> : submissions.length === 0 ? <section className="trust-config-panel"><p className="eyebrow">No submissions yet</p><h2>Clinical reviewer inbox is empty</h2><p>Completed reviewer scopes will appear here after the reviewer chooses Submit for review.</p></section> : submissions.map((submission) => {
          const id = submission.reviewerUserId;
          const canApprove = submission.status === 'submitted';
          const canReturn = submission.status === 'submitted' || submission.status === 'approved';
          return <section className="trust-config-panel" key={id || submission.reviewerEmail}>
            <div className="trust-panel-heading"><div><p className="eyebrow">{statusLabel(submission.status)}</p><h2>{submission.reviewerEmail || 'Clinical reviewer'}</h2><p>{submission.assignedRoleKeys?.length || 0} assigned clinical roles</p></div>{submission.status === 'approved' && <CheckCircle2/>}</div>
            <div className="meta-row"><span>Submitted: {formatDate(submission.submittedAt)}</span><span>Updated: {formatDate(submission.updatedAt)}</span></div>
            {submission.reviewComment && <p><strong>Latest review comment:</strong> {submission.reviewComment}</p>}
            {canReturn && <label>Return comment<textarea rows="3" value={comments[id] || ''} onChange={(event)=>setComments((current)=>({...current,[id]:event.target.value}))} placeholder="Explain what needs to change"/></label>}
            {(canApprove || canReturn) && <div className="trust-actions">{canReturn && <button className="button secondary" disabled={busyId === id} onClick={()=>act(submission,'request_submission_changes')}><Undo2 size={17}/>Return for changes</button>}{canApprove && <button className="button primary" disabled={busyId === id} onClick={()=>act(submission,'approve_submission')}><ShieldCheck size={17}/>Approve clinical review</button>}</div>}
          </section>;
        })}
      </main>
    </div>
  </div>;
}
